<template>
  <div v-if="displayBadges.length" class="wc-product-badges">
    <span v-for="badge in displayBadges" :key="badge.key" class="wc-badge" :class="badge.class">
      <span class="wc-badge__dot" :class="`wc-badge__dot--${badge.dotColor}`"></span>
      <span class="wc-badge__label">{{ badge.label }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { inject, computed, defineProps } from 'vue';
import { PRODUCT_AVAILABILITY_STATUS } from '@/constants/AppConstants';
import { configurationKey } from '@/injections';
import {
  PROMO_RIBBON_TYPE,
  RIBBON_CONFIG_MODERN,
} from '../../constants/PromoRibbonConstantsModern';
// ..
const BADGE_PRIORITY = {
  OUT_OF_STOCK: 1,
  SHOW_QUANTITY: 2,
  LOW_STOCK: 3,
  AUTO_DISCOUNT: 4,
  SALE: 5,
};

const MAX_BADGES = 2;

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
});

const configuration = inject(configurationKey);

const discountConfig = RIBBON_CONFIG_MODERN[PROMO_RIBBON_TYPE.DISCOUNT_NAME_RIBBON];
const saleConfig = RIBBON_CONFIG_MODERN[PROMO_RIBBON_TYPE.SALE_AMOUNT_RIBBON];

const displayBadges = computed(() => {
  const product = props.product;
  const discountCanDisplay = discountConfig.canDisplay(product);
  const saleCanDisplay = saleConfig.canDisplay(product);
  return [
    {
      key: 'out',
      priority: BADGE_PRIORITY.OUT_OF_STOCK,
      class: 'wc-badge--out-of-stock',
      dotColor: 'danger',
      label: configuration?.outOfStockTerm ?? '',
      canDisplay: product.availability === PRODUCT_AVAILABILITY_STATUS.out,
    },
    {
      key: 'showQuantity',
      priority: BADGE_PRIORITY.SHOW_QUANTITY,
      class: 'wc-badge--show-quantity',
      dotColor: 'warning-light',
      label: `${product.onHand} ${configuration?.inStockTerm ?? ''}`,
      canDisplay: product.availability === PRODUCT_AVAILABILITY_STATUS.showQuantity,
    },
    {
      key: 'low',
      priority: BADGE_PRIORITY.LOW_STOCK,
      class: 'wc-badge--low-stock',
      dotColor: 'warning-light',
      label: configuration?.limitedAvailabilityTerm ?? '',
      canDisplay: product.availability === PRODUCT_AVAILABILITY_STATUS.low,
    },
    {
      key: 'autoDiscount',
      priority: BADGE_PRIORITY.AUTO_DISCOUNT,
      class: 'wc-badge--discount',
      dotColor: 'warning-dark',
      label: discountCanDisplay ? discountConfig.getTextContent(product) : '',
      canDisplay: discountCanDisplay,
    },
    {
      key: 'sale',
      canDisplay: saleCanDisplay,
      priority: BADGE_PRIORITY.SALE,
      class: 'wc-badge--sale',
      dotColor: 'success-dark',
      label: saleCanDisplay ? saleConfig.getTextContent(product) : '',
    },
  ]
    .filter(badge => badge.canDisplay)
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MAX_BADGES);
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-product-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.wc-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.4375rem;
  border-radius: 1.25rem;
  font-size: 0.625rem;
  font-weight: 600;
  line-height: 1.25;
  background-color: var(--gray-100, $gray-100);
  color: var(--gray-700, $gray-700);
  min-width: 0;
  max-width: 6.875rem;
  overflow: hidden;

  &--out-of-stock {
    background-color: var(--danger-lighter-9);
    color: var(--danger-darker-2);
  }

  &--show-quantity,
  &--low-stock {
    background-color: var(--warning-lighter-8);
    color: var(--warning-darker-5);
  }

  &--discount {
    background-color: var(--warning-lighter-9);
    color: var(--warning-darker-5);
  }

  &--sale {
    background-color: $product-card-badge-success-color;
    color: var(--dark);
  }
}

.wc-badge__dot {
  display: inline-block;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  flex-shrink: 0;

  &--danger {
    background-color: var(--danger-darker-1);
  }

  &--warning-light {
    background-color: var(--warning-lighter-1);
  }

  &--warning-dark {
    background-color: var(--warning-darker-5);
  }

  &--success-dark {
    background-color: var(--dark);
  }
}

.wc-badge__label {
  // flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
